import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styles/theme';
import DraggableDialog from 'components/common/DraggableDialog';
import { showAlert } from 'utils/toastBridge';
import { useGridExportRegistry } from './GridExportContext';
import {
  buildMergedAoa,
  buildSingleAoa,
  buildSingleCellMergeSheets,
  getExportColumnDefs,
  handleHasCellMergeColumns,
  writeAoaToXlsx,
  writeSheetsToXlsx,
} from './excelExportUtils';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
`;

const Guide = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

const ModeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModeOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
`;

const Section = styled.div`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: 10px 12px;
  min-height: 0;
  overflow: auto;
  flex: ${(p) => (p.$flex === false ? '0 0 auto' : '1')};
`;

const SectionTitle = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  margin-bottom: 8px;
  color: ${theme.colors.text.primary};
`;

const CheckItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${theme.typography.fontSize.sm};
  padding: 4px 0;
  color: ${theme.colors.text.primary};
  cursor: ${(p) => (p.$locked ? 'default' : 'pointer')};
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  line-height: 1.4;
`;

const GridGroup = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const GridGroupTitle = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  margin-bottom: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid ${theme.colors.border};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const Btn = styled.button`
  padding: 6px 14px;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$primary ? theme.colors.primary : theme.colors.white)};
  color: ${(p) => (p.$primary ? theme.colors.white : theme.colors.text.primary)};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 500;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/**
 * 엑셀 다운로드 다이얼로그
 * - 현재 포커스 그리드만 / 그리드 병합
 * - 본문 셀 병합 컬럼이 있으면: 셀병합·병합해제 시트를 한 파일에 선택 저장
 * - 병합 시 컬럼 멀티선택 (required 는 해제 불가)
 */
const ExcelExportDialog = ({
  open,
  onClose,
  fileName = 'grid_export',
}) => {
  const registry = useGridExportRegistry();
  const grids = registry.list();
  const focused = registry.getFocused();
  const canMerge = grids.length >= 2;

  const [mode, setMode] = useState('current'); // current | merge
  const [step, setStep] = useState(1); // 1: mode(+grids)  2: columns (merge only)
  const [selectedGridIds, setSelectedGridIds] = useState([]);
  const [selectedColKeys, setSelectedColKeys] = useState([]);
  /** 본문 셀 병합 시트 포함 여부 */
  const [includeCellMerged, setIncludeCellMerged] = useState(true);
  /** 병합 해제(원본 반복) 시트 포함 여부 */
  const [includeCellUnmerged, setIncludeCellUnmerged] = useState(true);

  useEffect(() => {
    if (!open) return;
    setMode('current');
    setStep(1);
    setIncludeCellMerged(true);
    setIncludeCellUnmerged(true);
    const focusId = focused?.id || grids[0]?.id;
    setSelectedGridIds(focusId ? [focusId] : []);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetHandles = useMemo(() => {
    if (mode === 'current') {
      return focused ? [focused] : grids.slice(0, 1);
    }
    return grids.filter((g) => selectedGridIds.includes(g.id));
  }, [mode, focused, grids, selectedGridIds]);

  const allColumns = useMemo(() => {
    const list = [];
    for (const h of targetHandles) {
      list.push(...getExportColumnDefs(h));
    }
    return list;
  }, [targetHandles]);

  const showCellMergeOptions = useMemo(
    () => mode === 'current' && targetHandles[0] && handleHasCellMergeColumns(targetHandles[0]),
    [mode, targetHandles]
  );

  // 대상 그리드/모드 변경 시 컬럼 기본값: 전체 선택 (required 포함)
  useEffect(() => {
    if (!open) return;
    if (mode === 'current' || step === 2) {
      setSelectedColKeys(allColumns.map((c) => c.key));
    }
  }, [open, mode, step, selectedGridIds.join('|'), focused?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGrid = (id) => {
    setSelectedGridIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const toggleCol = (col) => {
    if (col.required) return;
    setSelectedColKeys((prev) =>
      prev.includes(col.key) ? prev.filter((k) => k !== col.key) : [...prev, col.key]
    );
  };

  const handleDownload = () => {
    if (!targetHandles.length) {
      showAlert('내보낼 그리드가 없습니다.', 'warning');
      return;
    }
    const cols = selectedColKeys.length
      ? selectedColKeys
      : allColumns.map((c) => c.key);

    // 필수 컬럼 강제 포함
    const requiredKeys = allColumns.filter((c) => c.required).map((c) => c.key);
    const finalKeys = Array.from(new Set([...requiredKeys, ...cols]));

    if (!finalKeys.length) {
      showAlert('내보낼 항목을 선택하세요.', 'warning');
      return;
    }

    if (showCellMergeOptions && !includeCellMerged && !includeCellUnmerged) {
      showAlert('셀 병합 / 병합 해제 중 하나 이상 선택하세요.', 'warning');
      return;
    }

    try {
      if (mode === 'current' && showCellMergeOptions) {
        const { sheets, rowCount } = buildSingleCellMergeSheets(targetHandles[0], finalKeys, {
          includeMerged: includeCellMerged,
          includeUnmerged: includeCellUnmerged,
        });
        if (!rowCount) {
          showAlert('다운로드할 데이터가 없습니다.', 'info');
          return;
        }
        writeSheetsToXlsx(sheets, `${fileName}_${targetHandles[0].label || 'grid'}`);
        onClose?.();
        return;
      }

      const { aoa, rowCount } =
        mode === 'current'
          ? buildSingleAoa(targetHandles[0], finalKeys)
          : buildMergedAoa(targetHandles, finalKeys);

      if (!rowCount) {
        showAlert('다운로드할 데이터가 없습니다.', 'info');
        return;
      }
      const suffix = mode === 'merge' ? '_병합' : `_${targetHandles[0].label || 'grid'}`;
      writeAoaToXlsx(aoa, `${fileName}${suffix}`);
      onClose?.();
    } catch (e) {
      showAlert(`엑셀 다운로드 중 오류: ${e?.message || String(e)}`, 'error');
    }
  };

  const onPrimary = () => {
    if (mode === 'merge' && step === 1) {
      if (selectedGridIds.length < 2) {
        showAlert('병합할 그리드를 2개 이상 선택하세요.', 'warning');
        return;
      }
      setStep(2);
      return;
    }
    handleDownload();
  };

  const columnsByGrid = useMemo(() => {
    const map = new Map();
    for (const c of allColumns) {
      if (!map.has(c.gridId)) map.set(c.gridId, { label: c.gridLabel, cols: [] });
      map.get(c.gridId).cols.push(c);
    }
    return Array.from(map.values());
  }, [allColumns]);

  const dialogHeight = (() => {
    if (mode === 'merge' && step === 2) return '640px';
    if (showCellMergeOptions) return '520px';
    return '420px';
  })();

  return (
    <DraggableDialog
      open={open}
      title="엑셀 다운로드"
      onClose={onClose}
      dialogWidth="520px"
      dialogHeight={dialogHeight}
      keepMounted={false}
    >
      <Body>
        <Guide>
          {mode === 'current' && (
            <>
              현재 선택된 그리드:
              <strong> {focused?.label || grids[0]?.label || '-'}</strong>
            </>
          )}
          {mode === 'merge' && step === 1 && (
            <>여러 그리드 데이터를 키로 결합(join)해 한 시트로 내려받습니다.</>
          )}
          {mode === 'merge' && step === 2 && (
            <>내보낼 항목을 선택하세요. <strong>*</strong> 필수 항목은 해제할 수 없습니다.</>
          )}
        </Guide>

        {step === 1 && (
          <ModeRow>
            <ModeOption>
              <input
                type="radio"
                name="excelMode"
                checked={mode === 'current'}
                onChange={() => setMode('current')}
              />
              <span>선택된 그리드만 다운로드</span>
            </ModeOption>
            <ModeOption $disabled={!canMerge}>
              <input
                type="radio"
                name="excelMode"
                checked={mode === 'merge'}
                disabled={!canMerge}
                onChange={() => canMerge && setMode('merge')}
              />
              <span>
                그리드 병합하여 다운로드
                {!canMerge ? ' (등록된 그리드가 2개 이상일 때 사용)' : ''}
              </span>
            </ModeOption>
          </ModeRow>
        )}

        {mode === 'merge' && step === 1 && (
          <Section>
            <SectionTitle>병합할 그리드</SectionTitle>
            {grids.map((g) => (
              <CheckItem key={g.id}>
                <input
                  type="checkbox"
                  checked={selectedGridIds.includes(g.id)}
                  onChange={() => toggleGrid(g.id)}
                />
                <span>{g.label}</span>
              </CheckItem>
            ))}
          </Section>
        )}

        {showCellMergeOptions && step === 1 && (
          <Section $flex={false}>
            <SectionTitle>셀 표시 방식 (한 파일 · 시트)</SectionTitle>
            <CheckItem>
              <input
                type="checkbox"
                checked={includeCellMerged}
                onChange={(e) => setIncludeCellMerged(e.target.checked)}
              />
              <span>셀 병합</span>
            </CheckItem>
            <CheckItem>
              <input
                type="checkbox"
                checked={includeCellUnmerged}
                onChange={(e) => setIncludeCellUnmerged(e.target.checked)}
              />
              <span>셀 병합 해제</span>
            </CheckItem>
            <Hint>
              둘 다 선택하면 한 엑셀 파일에 「셀병합」「병합해제」 시트가 함께 저장됩니다.
            </Hint>
          </Section>
        )}

        {(mode === 'current' || (mode === 'merge' && step === 2)) && (
          <Section>
            <SectionTitle>내보낼 항목</SectionTitle>
            {columnsByGrid.map((group) => (
              <GridGroup key={group.label}>
                {columnsByGrid.length > 1 && (
                  <GridGroupTitle>{group.label}</GridGroupTitle>
                )}
                {group.cols.map((c) => (
                  <CheckItem key={c.key} $locked={c.required}>
                    <input
                      type="checkbox"
                      checked={selectedColKeys.includes(c.key)}
                      disabled={c.required}
                      onChange={() => toggleCol(c)}
                    />
                    <span>
                      {c.required ? `*${c.header.replace(/^\*/, '')}` : c.header.replace(/^\*/, '')}
                    </span>
                  </CheckItem>
                ))}
              </GridGroup>
            ))}
            {!allColumns.length && (
              <Guide>표시할 컬럼이 없습니다.</Guide>
            )}
          </Section>
        )}

        <Footer>
          <Btn type="button" onClick={onClose}>취소</Btn>
          {mode === 'merge' && step === 2 && (
            <Btn type="button" onClick={() => setStep(1)}>이전</Btn>
          )}
          <Btn type="button" $primary onClick={onPrimary}>
            {mode === 'merge' && step === 1 ? '다음' : '다운로드'}
          </Btn>
        </Footer>
      </Body>
    </DraggableDialog>
  );
};

export default ExcelExportDialog;
